import React from 'react';
import { 
  Box, 
  Card, 
  CardMedia, 
  Typography, 
  Button
} from '@mui/material';
import { ThumbUpAlt } from '@mui/icons-material';
import { Deal } from '../services/deals';

// Fonction utilitaire pour la couleur des notes
export const getRatingColor = (percent?: number): string => {
  if (!percent) return '#888888';
  if (percent >= 85) return '#66BB6A'; // Vert pour très positif
  if (percent >= 70) return '#8BC34A'; // Vert clair pour positif
  if (percent >= 50) return '#FFC107'; // Jaune pour plutôt positif
  if (percent >= 40) return '#FF9800'; // Orange pour mitigé
  return '#F44336'; // Rouge pour négatif
};

export const getStoreIconUrl = (source?: string) => {
  switch (source) {
    case 'epic': return "https://upload.wikimedia.org/wikipedia/commons/3/31/Epic_Games_logo.svg";
    case 'gog': return "https://upload.wikimedia.org/wikipedia/commons/2/2e/GOG.com_logo.svg";
    case 'steam': return "https://upload.wikimedia.org/wikipedia/commons/8/83/Steam_icon_logo.svg";
    default: return null;
  }
};

export const renderStoreLabel = (source?: string) => {
  switch (source) {
    case 'epic': return 'Epic Games Store';
    case 'gog': return 'GOG.com';
    case 'steam': return 'Steam';
    case 'fanatical': return 'Fanatical';
    case 'gamesplanet': return 'Gamesplanet';
    default: return source || 'Boutique';
  }
};

export const renderStoreIcon = (source?: string) => {
  const iconUrl = getStoreIconUrl(source);
  if (iconUrl) {
    return (
      <Box 
        component="img"
        src={iconUrl}
        alt={source}
        sx={{ 
          width: 24, 
          height: 24, 
          filter: source === 'gog' ? 'none' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))',
          bgcolor: source === 'gog' ? 'white' : 'transparent',
          borderRadius: source === 'gog' ? '50%' : 0,
          p: source === 'gog' ? 0.5 : 0
        }}
      />
    );
  }
  // Fallback text badges
  if (source === 'fanatical') {
      return (
          <Box sx={{ bgcolor: '#00A9F4', color: 'white', px: 0.5, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 'bold' }}>FAN</Box>
      )
  }
  if (source === 'gamesplanet') {
      return (
          <Box sx={{ bgcolor: '#fa5c1c', color: 'white', px: 0.5, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 'bold' }}>GP</Box>
      )
  }
  return null;
};

interface DealCardProps {
  deal: Deal;
  onClick: (deal: Deal) => void;
  sx?: any;
}

const DealCard: React.FC<DealCardProps> = ({ deal, onClick, sx }) => {
  return (
    <Card 
      onClick={() => onClick(deal)}
      sx={{ 
        aspectRatio: '16/9',
        display: 'flex', 
        flexDirection: 'column', 
        position: 'relative',
        textDecoration: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'rgba(30, 30, 30, 0.8)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 8,
          zIndex: 1,
          '& .bg-image': {
            filter: 'brightness(0.9)'
          }
        },
        ...sx
      }}
    >
      {/* Image de fond */}
      {deal.img ? (
        <CardMedia 
          className="bg-image"
          component="img"
          image={deal.img} 
          alt={deal.title}
          loading="lazy"
          sx={{ 
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            filter: 'brightness(0.7)',
            transition: 'filter 0.3s',
          }} 
        />
      ) : (
        <Box 
          sx={{ 
            height: '100%', 
            width: '100%',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Image non disponible
          </Typography>
        </Box>
      )}

      {/* Badge Source */}
      <Box sx={{ 
        position: 'absolute', 
        right: 8, 
        top: 8, 
        zIndex: 2,
        display: 'flex',
        gap: 0.5
      }}>
        {Array.from(new Set([deal.source, ...(deal.otherDeals?.map(d => d.source) || [])].filter(Boolean))).map((source, i) => (
          <Box key={i}>
              {renderStoreIcon(source)}
          </Box>
        ))}
      </Box>

      {/* Overlay Contenu */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        p: 2,
        background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.8) 50%, rgba(0,0,0,0))',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5
      }}>
        <Typography 
          variant="subtitle1" 
          component="div" 
          sx={{ 
            color: 'white',
            fontWeight: 'bold', 
            lineHeight: 1.2,
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            // Nettoyage visuel du titre (similaire à DealsCarousel)
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {deal.title.replace(/\s*\(-?\d+%\)\s*$/, '').replace(/\s*\(GRATUIT\)\s*$/, '')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          {/* Prix */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {deal.price !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {deal.price === 0 ? (
                          <Typography variant="body2" sx={{ color: '#66BB6A', fontWeight: 'bold' }}>
                          GRATUIT
                          </Typography>
                      ) : (
                          <>
                          <Typography variant="body2" sx={{ color: '#66BB6A', fontWeight: 'bold' }}>
                              {deal.price.toFixed(2)}€
                          </Typography>
                          {deal.originalPrice && deal.originalPrice > deal.price && (
                              <Typography 
                              variant="caption" 
                              sx={{ ml: 1, textDecoration: 'line-through', color: '#aaa' }}
                              >
                              {deal.originalPrice.toFixed(2)}€
                              </Typography>
                          )}
                          </>
                      )}
                  </Box>
              )}
          </Box>

          {/* Évaluation (Tous stores) */}
          {deal.rating && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                      variant="caption" 
                      sx={{
                      color: getRatingColor(deal.ratingPercent),
                      fontWeight: 'medium',
                      display: 'flex',
                      alignItems: 'center',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                      }}
                  >
                      <ThumbUpAlt sx={{ fontSize: 14, mr: 0.5 }} />
                      {deal.ratingPercent ? `${deal.ratingPercent}%` : ''}
                  </Typography>
              </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default DealCard;
